import { useId, useState, type JSX, type ReactNode } from 'react';

/**
 * A password box you can look at.
 *
 * Typing a long password blind and being told only afterwards that it was
 * wrong — or worse, that two of them did not match — is a bad experience for
 * anyone and a worse one for a child. Every field here asks for at least ten
 * characters, so the chance of a silent typo is not small.
 *
 * It starts hidden, always. Revealing is a deliberate act by the person at the
 * keyboard, and the state is never remembered: navigating away and coming back
 * gives a covered field again, because the risk this protects against is
 * somebody standing behind you rather than anything on the wire.
 *
 * The label is associated explicitly rather than by wrapping. A button nested
 * inside a `<label>` is activated twice — once as itself and once by the label
 * — which is exactly the kind of detail that makes a reveal toggle feel broken.
 */
export function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  autoFocus = false,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  autoFocus?: boolean;
  /** Hints, strength meters and errors, rendered under the input. */
  children?: ReactNode;
}): JSX.Element {
  const id = useId();
  const [visible, setVisible] = useState(false);

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>

      <div className="field__control">
        <input
          id={id}
          className="field__input"
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
        />
        <button
          type="button"
          className="field__reveal"
          onClick={() => setVisible((shown) => !shown)}
          aria-pressed={visible}
          aria-controls={id}
          /* The visible word is the start of the accessible name, so speech
             control and the label agree (WCAG 2.5.3). */
          aria-label={visible ? 'Hide password' : 'Show password'}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>

      {children}
    </div>
  );
}
